import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { inr0, inr, fmtDate } from "@/lib/format";
import { Stat } from "@/components/common";
import { Card } from "@/components/ui/card";
import {
  Building2, Users, TrendingUp, CheckCircle2, AlertTriangle, Home, DoorOpen, Wallet, TrendingDown,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, BarChart, Bar, Legend,
} from "recharts";
import { useNavigate } from "react-router-dom";

const money = (v) => (v >= 100000 ? `₹${(v / 100000).toFixed(1)}L` : v >= 1000 ? `₹${(v / 1000).toFixed(0)}k` : `₹${v}`);

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [rem, setRem] = useState({ expiring: [], overdue: [], due_soon: [] });
  const nav = useNavigate();
  useEffect(() => {
    api.get("/dashboard").then(setD).catch(() => {});
    api.get("/reminders").then(setRem).catch(() => {});
  }, []);
  if (!d) return <div className="text-slate-400">Loading dashboard…</div>;
  const k = d.kpis, c = d.collection;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Dashboard</h1>
        <p className="text-slate-500 text-sm mt-1">Your rental portfolio at a glance · {fmtDate(new Date())}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat testid="kpi-properties" label="Total Properties" value={k.total_properties} hint={`${k.total_units} units total`} icon={Building2} tone="brand" to="/properties" />
        <Stat testid="kpi-units" label="Total Units" value={k.total_units} hint={`${k.occupied} occupied · ${k.vacant} vacant`} icon={Home} to="/properties" />
        <Stat testid="kpi-tenants" label="Active Tenants" value={k.active_tenants} icon={Users} to="/tenants" />
        <Stat testid="kpi-expected" label="Monthly Rent Expected" value={inr0(k.monthly_expected)} icon={TrendingUp} to="/agreements" />
        <Stat testid="kpi-received" label="Rent Received (Month)" value={inr0(k.received_month)} icon={CheckCircle2} tone="success" to="/payments" />
        <Stat testid="kpi-outstanding" label="Outstanding Rent" value={inr0(k.outstanding)} icon={AlertTriangle} tone="danger" to="/outstanding" />
        <Stat testid="kpi-occupied" label="Occupied Units" value={k.occupied} icon={Home} tone="success" to="/properties" />
        <Stat testid="kpi-vacant" label="Vacant Units" value={k.vacant} icon={DoorOpen} tone="warning" to="/vacant-units" />
        <Stat testid="kpi-net" label="Net Income (Month)" value={inr0(k.net_month)} icon={Wallet} tone={k.net_month >= 0 ? "success" : "danger"} to="/reports" />
      </div>

      {/* Rent collection */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Stat label="Due This Month" value={inr0(c.due)} tone="brand" testid="kpi-due-month" to="/invoices" />
        <Stat label="Received" value={inr0(c.received)} tone="success" testid="kpi-coll-received" to="/payments" />
        <Stat label="Pending" value={inr0(c.pending)} tone="warning" testid="kpi-coll-pending" to="/outstanding" />
        <Stat label="Overdue" value={inr0(c.overdue)} tone="danger" testid="kpi-coll-overdue" to="/outstanding" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 p-5 border-slate-200 shadow-sm">
          <h3 className="font-heading text-lg font-semibold text-slate-900 mb-4">Income vs Expense (6 months)</h3>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={d.trend} margin={{ left: -10 }}>
              <defs>
                <linearGradient id="gInc" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#4338CA" stopOpacity={0.3} /><stop offset="95%" stopColor="#4338CA" stopOpacity={0} /></linearGradient>
                <linearGradient id="gExp" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#DC2626" stopOpacity={0.25} /><stop offset="95%" stopColor="#DC2626" stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={money} tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} />
              <Tooltip formatter={(v) => inr(v)} contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 13 }} />
              <Legend />
              <Area type="monotone" dataKey="income" stroke="#4338CA" strokeWidth={2} fill="url(#gInc)" name="Income" />
              <Area type="monotone" dataKey="expense" stroke="#DC2626" strokeWidth={2} fill="url(#gExp)" name="Expense" />
            </AreaChart>
          </ResponsiveContainer>
        </Card>
        <Card className="p-5 border-slate-200 shadow-sm">
          <h3 className="font-heading text-lg font-semibold text-slate-900 mb-4">Property-wise Income / Expense</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={d.property_chart} layout="vertical" margin={{ left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
              <XAxis type="number" tickFormatter={money} tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="name" width={80} tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} />
              <Tooltip formatter={(v) => inr(v)} contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 13 }} />
              <Bar dataKey="income" fill="#059669" radius={[0, 4, 4, 0]} name="Income" />
              <Bar dataKey="expense" fill="#DC2626" radius={[0, 4, 4, 0]} name="Expense" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-5 border-slate-200 shadow-sm" data-testid="alerts-overdue">
          <h3 className="font-heading text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-rose-500" /> Overdue Rent</h3>
          {rem.overdue.length === 0 ? <p className="text-sm text-slate-400">No overdue invoices 🎉</p> : (
            <div className="space-y-2">
              {rem.overdue.slice(0, 6).map((o) => (
                <div key={o.invoice_id} onClick={() => nav("/outstanding")} className="flex items-center justify-between p-3 rounded-md bg-rose-50 border border-rose-100 cursor-pointer hover:bg-rose-100 transition-colors">
                  <div><p className="text-sm font-medium text-slate-800">{o.tenant} · {o.unit || o.property}</p><p className="text-xs text-rose-600">{o.invoice_no} · overdue {Math.abs(o.days)} days</p></div>
                  <span className="font-heading font-semibold text-rose-600">{inr0(o.balance)}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
        <Card className="p-5 border-slate-200 shadow-sm" data-testid="alerts-expiring">
          <h3 className="font-heading text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2"><TrendingDown className="w-5 h-5 text-amber-500" /> Agreements Expiring Soon</h3>
          {rem.expiring.length === 0 ? <p className="text-sm text-slate-400">No agreements expiring soon.</p> : (
            <div className="space-y-2">
              {rem.expiring.slice(0, 6).map((o) => (
                <div key={o.lease_id} onClick={() => nav("/agreements")} className="flex items-center justify-between p-3 rounded-md bg-amber-50 border border-amber-100 cursor-pointer hover:bg-amber-100 transition-colors">
                  <div><p className="text-sm font-medium text-slate-800">{o.tenant} · {o.unit || o.property}</p><p className="text-xs text-amber-600">Expires {fmtDate(o.end_date)}</p></div>
                  <span className="font-heading font-semibold text-amber-600">{o.days} days</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
